import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { Address, AddressFormData, Province, Ward } from '../models/AddressModel';
import { 
  getAddresses, 
  getAddress, 
  createAddress as createAddressAPI, 
  updateAddress as updateAddressAPI, 
  deleteAddress as deleteAddressAPI,
  getProvinces,
  getWardsByProvince
} from './AddressCRUD';

// Async thunks for addresses
export const fetchAddresses = createAsyncThunk(
  'address/fetchAddresses',
  async () => {
    const response = await getAddresses();
    return response.data;
  }
);

export const fetchAddressById = createAsyncThunk(
  'address/fetchAddressById',
  async (id: string) => {
    const response = await getAddress(id);
    return response.data;
  }
);

export const createAddress = createAsyncThunk(
  'address/createAddress',
  async (data: AddressFormData) => {
    const response = await createAddressAPI(data);
    return response.data;
  }
);

export const updateAddress = createAsyncThunk(
  'address/updateAddress',
  async ({ id, data }: { id: string; data: AddressFormData }) => {
    const response = await updateAddressAPI(id, data);
    return response.data;
  }
);

export const deleteAddress = createAsyncThunk(
  'address/deleteAddress',
  async (id: string) => {
    await deleteAddressAPI(id);
    return id;
  }
);

// Async thunks for provinces and wards
export const fetchProvinces = createAsyncThunk(
  'address/fetchProvinces',
  async () => {
    const response = await getProvinces();
    return response.data;
  }
);

export const fetchWardsByProvince = createAsyncThunk(
  'address/fetchWardsByProvince',
  async (provinceId: string) => {
    const response = await getWardsByProvince(provinceId);
    return { provinceId, wards: response.data };
  }
);

// State interface
interface AddressState {
  addresses: Address[];
  provinces: Province[];
  wards: { [provinceId: string]: Ward[] };
  selectedAddress: Address | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: AddressState = {
  addresses: [],
  provinces: [],
  wards: {},
  selectedAddress: null,
  loading: false,
  error: null,
};

const normalizeListPayload = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown[] }).results)) {
    return (payload as { results: T[] }).results;
  }

  return [];
};

// Slice
const addressSlice = createSlice({
  name: 'address',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedAddress: (state) => {
      state.selectedAddress = null;
    },
    setSelectedAddress: (state, action: PayloadAction<Address>) => {
      state.selectedAddress = action.payload;
    },
    clearWards: (state) => {
      state.wards = {};
    },
  },
  extraReducers: (builder) => {
    // Fetch addresses
    builder
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = action.payload;
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch addresses';
      });

    // Fetch address by ID
    builder
      .addCase(fetchAddressById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddressById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAddress = action.payload;
      })
      .addCase(fetchAddressById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch address';
      });

    // Create address
    builder
      .addCase(createAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses.push(action.payload);
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create address';
      });

    // Update address
    builder
      .addCase(updateAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.addresses.findIndex(address => address.id === action.payload.id);
        if (index !== -1) {
          state.addresses[index] = action.payload;
        }
        if (state.selectedAddress?.id === action.payload.id) {
          state.selectedAddress = action.payload;
        }
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update address';
      });

    // Delete address
    builder
      .addCase(deleteAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = state.addresses.filter(address => address.id !== action.payload);
        if (state.selectedAddress?.id === action.payload) {
          state.selectedAddress = null;
        }
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete address';
      });

    // Fetch provinces
    builder
      .addCase(fetchProvinces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProvinces.fulfilled, (state, action) => {
        state.loading = false;
        state.provinces = normalizeListPayload<Province>(action.payload);
      })
      .addCase(fetchProvinces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch provinces';
      });

    // Fetch wards by province
    builder
      .addCase(fetchWardsByProvince.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWardsByProvince.fulfilled, (state, action) => {
        state.loading = false;
        state.wards[action.payload.provinceId] = normalizeListPayload<Ward>(action.payload.wards);
      })
      .addCase(fetchWardsByProvince.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch wards';
      });
  },
});

// Export actions
export const { clearError, clearSelectedAddress, setSelectedAddress, clearWards } = addressSlice.actions;

// Export reducer
export default addressSlice.reducer;

// Export selectors
export const selectAddresses = (state: { address: AddressState }) => state.address.addresses;
export const selectProvinces = (state: { address: AddressState }) => normalizeListPayload<Province>(state.address.provinces);
export const selectWardsByProvince = createSelector(
  [(state: { address: AddressState }) => state.address.wards, (_, provinceId: string) => provinceId],
  (wards, provinceId) => normalizeListPayload<Ward>(wards[provinceId] || [])
);
export const selectSelectedAddress = (state: { address: AddressState }) => state.address.selectedAddress;
export const selectAddressLoading = (state: { address: AddressState }) => state.address.loading;
export const selectAddressError = (state: { address: AddressState }) => state.address.error; 